import React, { useState } from 'react';
import { Mail, Check, AlertCircle } from 'lucide-react';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { Translations } from '../utils/translations';

interface NewsletterSubscriptionProps {
  t: Translations;
  locale: string;
  campaignId?: string;
  region?: string;
}

export const NewsletterSubscription: React.FC<NewsletterSubscriptionProps> = ({
  t,
  locale,
  campaignId,
  region
}) => {
  const [email, setEmail] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setErrorMessage(t.missingEmailMessage);
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      setStatus('error');
      setErrorMessage(t.invalidEmailMessage);
      return;
    }

    if (!agreedToPrivacy) {
      setStatus('error');
      setErrorMessage(t.privacyConsentReminderMessage);
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const { error } = await DataService.createSubscriber({
        email: email.trim(),
        agreed_to_privacy: agreedToPrivacy,
        source: 'public_campaign',
        campaign_id: campaignId,
        region: region
      });

      if (error) {
        setStatus('error');
        // Show specific error message for duplicate emails
        if (error.includes('already subscribed')) {
          setErrorMessage(error);
        } else {
          setErrorMessage(t.newsletterError);
        }
      } else {
        setStatus('success');
        setEmail('');
        setAgreedToPrivacy(false);
      }
    } catch {
      setStatus('error');
      setErrorMessage(t.newsletterError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-4 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
          {t.newsletterTitle}
        </h3>

        {status === 'success' ? (
          <div className="bg-white/20 rounded-xl p-6 max-w-md mx-auto">
            <div className="flex items-center justify-center mb-3">
              <div className="bg-green-500 p-2 rounded-full">
                <Mail className="text-white" size={24} />
              </div>
            </div>
            <p className="text-white font-medium mb-2">Please check your email!</p>
            <p className="text-white/80 text-sm">
              We've sent a confirmation link to your email address. Click the link to complete your subscription.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletterPlaceholder}
                className="flex-1 px-4 py-3 rounded-lg border-2 border-transparent focus:border-white/50 focus:outline-none text-gray-900 placeholder-gray-500"
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '...' : t.newsletterSubscribe}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 mb-3">
              <div 
                className="flex items-center cursor-pointer"
                onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
              >
                <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                  agreedToPrivacy 
                    ? 'bg-white border-white' 
                    : 'border-white/60 bg-transparent'
                }`}>
                  {agreedToPrivacy && (
                    <Check className="text-purple-600" size={14} />
                  )}
                </div>
              </div>
              <label 
                className="text-white/90 text-sm cursor-pointer select-none"
                onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
              >
                {t.agreeWithPrivacy}{' '}
                <a
                  href={`/privacy-policy?locale=${locale}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t.privacyPolicy}
                </a>
              </label>
            </div>

            {status === 'error' && errorMessage && (
              <div className="flex items-center justify-center gap-2 text-red-200 text-sm mt-2">
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
